// FE-SCRM-060 — <CrmSearchInput> + ?q= URL codec, debounced 250ms.
//
// Example:
//
//     // Inline at the top of any list panel
//     <CrmSearchInput placeholder="Search prospects" />
//     // The list query reads ``q = readSearchFromUrl(searchParams)``
//     // and re-fetches on URL changes. Page resets on every ?q
//     // change so the user doesn't land on an empty page-N.
//
// Used by the global ⌘+K modal (FE-SCRM-054) as the inner input
// too; the keyboard hookup lives in -054's modal wrapper.

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";


const DEBOUNCE_MS = 250;
const URL_KEY = "q";


export function readSearchFromUrl(params: URLSearchParams): string {
  return (params.get(URL_KEY) ?? "").trim();
}


export function writeSearchToParams(
  value: string, base: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(base);
  out.delete(URL_KEY);
  const trimmed = value.trim();
  if (trimmed) out.set(URL_KEY, trimmed);
  return out;
}


export interface CrmSearchInputProps {
  placeholder?: string;
  className?: string;
  /** Override the 250 ms debounce. Tests sometimes pass 0. */
  debounceMs?: number;
}


export function CrmSearchInput({
  placeholder = "Search…",
  className,
  debounceMs = DEBOUNCE_MS,
}: CrmSearchInputProps): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = readSearchFromUrl(searchParams);
  const [local, setLocal] = useState(urlValue);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state with URL changes from outside (e.g. clear button
  // elsewhere on the page). Don't run on every keystroke.
  useEffect(() => {
    setLocal(urlValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlValue]);

  const flush = useCallback(
    (next: string) => {
      const params = writeSearchToParams(next, searchParams);
      // Search changes the result set so ``?page`` is stale.
      params.delete("page");
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setLocal(next);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => flush(next), debounceMs);
    },
    [debounceMs, flush],
  );

  const onClear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setLocal("");
    flush("");
  }, [flush]);

  // Cleanup the pending timer on unmount so a tab change doesn't
  // fire a stale URL write after we've left the page.
  useEffect(
    () => () => { if (timer.current) clearTimeout(timer.current); },
    [],
  );

  return (
    <div
      className={
        "relative inline-flex items-center "
        + (className ?? "")
      }
    >
      <input
        type="search"
        role="searchbox"
        value={local}
        onChange={onChange}
        placeholder={placeholder}
        className={
          "w-full rounded-md border border-gray-300 bg-white px-3 "
          + "py-1.5 pr-8 text-sm placeholder-gray-400 "
          + "focus:border-indigo-500 focus:outline-none "
          + "focus:ring-1 focus:ring-indigo-500"
        }
      />
      {local !== "" && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={onClear}
          className={
            "absolute right-2 inline-flex h-5 w-5 items-center "
            + "justify-center rounded text-gray-400 hover:bg-gray-100 "
            + "hover:text-gray-600"
          }
        >
          ×
        </button>
      )}
    </div>
  );
}
